"use client";

import { Button } from "@2u-landing-pages/components/Button";
import { Icon } from "@2u-landing-pages/components/Icon";
import { TemplateWrapper } from "@2u-landing-pages/components/TemplateWrapper";
import { usePageState, useParseMeta, useProductCardHighlight } from "@2u-landing-pages/providers";
import { renderEmbeddedEntry, renderText } from "@2u-landing-pages/utils/contentful-richtext";
import EventClient from "@2u-landing-pages/utils/event-client";
import { scrollToElementById } from "@2u-landing-pages/utils/scrollToElement";
import { getIconSelectionFromFields } from "@2u-landing-pages/utils/icon-selection";
import { documentToReactComponents } from "@contentful/rich-text-react-renderer";
import { BLOCKS, INLINES } from "@contentful/rich-text-types";
import { Body, Container, Heading, useCountdown } from "@red-digital/bricks";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { BannerProps, CustomCSSProperties } from "../../Banner.types";
import { TimeBlock } from "./TimeBlock";

import cn from "classnames";
// @ts-ignore
import bannerStyles from "../../Banner.module.scss";
// @ts-ignore
import ltoStyles from "./LTO.module.scss";

const LTO: React.FC<Omit<BannerProps, "variation">> = ({
  sticky,
  main_content,
  button,
  background_color,
  inspector,
  className,
  end_date = "",
  countdown_background_color,
  countdown_text_color,
  location,
  mobile_orientation = "Vertical",
  sticky_type = "Full Banner",
  phoneNumber,
  stripFocusContext,
  style: defaultStyle,
}) => {
  const [hideContent, setHideContent] = useState<boolean>(false);
  const { months, days, hours, minutes, seconds } = useCountdown(end_date);
  const { dispatch } = usePageState();
  const { setHighlightedCardId } = useProductCardHighlight();
  const parseMetadata = useParseMeta();
  const lastHideRef = useRef(false);

  const handleHideContent = useCallback(() => {
    const scrollY = window.scrollY ?? document.documentElement.scrollTop;
    const isMobile = window.matchMedia(`(max-width: 1023px)`).matches;
    const bottomContent = Boolean(end_date) || Boolean(button);

    if (!isMobile || !bottomContent) {
      if (lastHideRef.current !== false) {
        lastHideRef.current = false;
        setHideContent(false);
      }
      return;
    }

    const HIDE_THRESHOLD = 140;
    const SHOW_THRESHOLD = 48;
    const next = scrollY > HIDE_THRESHOLD ? true : scrollY < SHOW_THRESHOLD ? false : lastHideRef.current;

    if (next !== lastHideRef.current) {
      lastHideRef.current = next;
      setHideContent(next);
    }
  }, [end_date, button]);

  useEffect(() => {
    if (!sticky || sticky_type !== "Half Banner") return;

    handleHideContent();
    window.addEventListener("scroll", handleHideContent, { passive: true });
    window.addEventListener("resize", handleHideContent);

    return () => {
      window.removeEventListener("scroll", handleHideContent);
      window.removeEventListener("resize", handleHideContent);
    };
  }, [sticky, sticky_type, handleHideContent]);

  if (!main_content && !end_date && !button) return null;

  const style: CustomCSSProperties = {
    ...defaultStyle,
  };

  const heading = main_content?.fields?.heading;
  const headingColor = main_content?.fields?.heading_color;
  const body = main_content?.fields?.body;
  const bodyColor = main_content?.fields?.body_color;
  const content = heading || body;
  const { icon: selectedIcon, size: selectedIconSize } = getIconSelectionFromFields(
    (main_content?.fields as Record<string, unknown>) || {},
    ["size"],
  );

  // Only apply colors if the text and color are defined
  if (heading && headingColor) style["--heading-color"] = `var(${headingColor})`;
  if (body && bodyColor) style["--body-color"] = `var(${bodyColor})`;

  if (countdown_background_color) style["--countdown-background-color"] = `var(${countdown_background_color})`;
  if (countdown_text_color) style["--countdown-text-color"] = `var(${countdown_text_color})`;

  const handleOnClick = (event: React.MouseEvent<HTMLAnchorElement, MouseEvent>) => {
    event.preventDefault();

    const { action, link_url } = button?.fields || {};

    if (action === "SLIDE_TO_ELEMENT") {
      if (link_url) {
        scrollToElementById(link_url);
      }
    } else if (action === "SCROLL_HIGHLIGHT_PRODUCT_CARD") {
      if (link_url) {
        scrollToElementById(link_url);
        setHighlightedCardId(link_url);
      }
    } else if (action === "BACK_TO_TOP") {
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else if (
      action === "CLOSE_DRAWER" ||
      action === "OPEN_CALENDLY" ||
      action === "OPEN_DRAWER" ||
      action === "OPEN_LINK" ||
      action === "OPEN_MODAL"
    ) {
      dispatch({ type: action });
    }

    EventClient.elementClicked({
      webElement: {
        elementType: "Landing Page",
        location,
        position: "CTA",
        text: button?.fields?.label || "",
      },
    });
  };
  return (
    <TemplateWrapper
      data-template="banner"
      {...(stripFocusContext ? { "data-strip-focus-context": stripFocusContext } : {})}
      backgroundColor={background_color || "--surface-primary-1"}
      className={cn(className, {
        [bannerStyles.sticky]: sticky,
      })}
      style={style}
    >
      <Container
        flex
        direction="column"
        justifyContent="center"
        alignItems="center"
        wrap="wrap"
        className={cn(bannerStyles.banner, hideContent && ltoStyles.bannerContentHidden)}
        style={style}
      >

          {main_content && (
          <Container flex lg={{ direction: "row" }} className={cn(ltoStyles.content, hideContent && ltoStyles.hidden)}>
            {selectedIcon && (
                <Icon
                className={ltoStyles.icon}
                name={selectedIcon}
                size={selectedIconSize}
              />
              )}
              {content &&
                typeof content === "object" &&
                documentToReactComponents(content, {
                  renderText: (text) => renderText(parseMetadata?.(text) || text, phoneNumber),
                  renderNode: {
                    [BLOCKS.PARAGRAPH]: (_, children) => {
                      const TextComponent = heading ? Heading : Body;
                      return (
                        <TextComponent
                          {...inspector({
                            entryId: main_content.sys.id,
                            fieldId: heading ? "heading" : "body",
                          })}
                          variant={heading ? "heading-sm" : "caption-lg"}
                          className={heading ? bannerStyles.heading : bannerStyles.body}
                        >
                          {children}
                          {!heading && body && Boolean(button) && (
                            <>
                              {" "}
                              <a href="#" onClick={handleOnClick}>
                                {button?.fields.label}
                              </a>
                            </>
                          )}
                        </TextComponent>
                      );
                    },
                    [INLINES.EMBEDDED_ENTRY]: (node) =>
                      renderEmbeddedEntry(node, "LTO Banner", content, phoneNumber, parseMetadata),
                  },
                })}
                    </Container>
        )}
        {(end_date || (heading && Boolean(button))) && (
          <Container
            flex
            direction={mobile_orientation == "Horizontal" ? "row" : "column"}
            justifyContent="center"
            alignItems="center"
            wrap="wrap"
            className={ltoStyles.countdownCta}
          >
            {end_date && (
                <div className={ltoStyles.countdown}>
                  {months !== 0 && (
                    <>
                      <TimeBlock label="Mon" value={months} />
                      <span className={ltoStyles.colon}>:</span>
                    </>
                  )}
                  <TimeBlock label="Days" value={days} />
                  <span className={ltoStyles.colon}>:</span>
                  <TimeBlock label="Hours" value={hours} />
                  <span className={ltoStyles.colon}>:</span>
                  <TimeBlock label="Min" value={minutes} />
                  <span className={ltoStyles.colon}>:</span>
                  <TimeBlock label="Sec" value={seconds} />
                </div>
              )}
              {heading && Boolean(button) && (
                <Button
                  location={location}
                  key={button?.sys.id}
                  {...button?.fields}
                  phoneNumber={phoneNumber}
                >
                  {button?.fields.label}
                </Button>
              )}
          </Container>
        )}
      </Container>
    </TemplateWrapper>
  );
};

export default LTO;
